import { Fragment, memo, useCallback, useMemo } from "react";

import { cn } from "@/lib/chillnn/cn";
import type {
	ReservationCancelFeeChargeInput,
	ReservationCancelInput,
	ReservationDetailForManagementReservationItemQuery,
	ReservationDetailUpdateHostMemoInput,
	ReservationDetailUpdateIssuedReceiptStatusInput,
	ReservationDetailUpdateMailMagazineStatusInput,
	ReservationDetailUpdateMailResendRequestInput,
	ReservationDetailUpdateNoShowStatusInput,
	ReservationLocalCancelFeeRejectInput,
	ReservationRefundRequestInput,
} from "@/lib/gql/__generated__/graphql";
import { StatusReservation } from "@/lib/gql/__generated__/graphql";

import { FeatureIslandContainer, FeatureIslandMain } from "@/components/features/_layouts";

import { Label } from "@/components/ui/label/label";

import {
	ApplyPriceText,
	ReservationDetailCancelModal,
	ReservationDetailChargeCancelFeeModal,
	ReservationDetailRefundRequest,
	TotalPeopleText,
} from "./components";
import {
	AdditionalFeesOfPrintComposition,
	BasicInfoOfPrintComposition,
	CancellationFeePaymentStatusOfOperationForReservationComposition,
	DateOfBasicInfoCompositions,
	DateOfRoomAndOptionCompositions,
	DatesOfManagementStatusCompositions,
	DeadItemsOfPrintComposition,
	DiscountOfPrintComposition,
	DiscountOfVoucherAndDiscountCompositions,
	EachDaysOfPrintComposition,
	EntryFormForGuestOfPrintComposition,
	GuestAddressOfBasicInfoCompositions,
	GuestEmailOfBasicInfoCompositions,
	GuestNameWithTitleOfBasicInfoCompositions,
	GuestStatusOfManagementStatusCompositions,
	GuestTelOfBasicInfoCompositions,
	HeaderComposition,
	HostMemoOfPrintComposition,
	IsMailMagazineAcceptOfBasicInfoCompositions,
	IssuedReceiptStatusOfManagementStatusCompositions,
	ListOfAdditionalFeesCompositions,
	ListOfBookedGuestSurveyCompositions,
	ListOfNonDateOptionCompositions,
	ListOfQuestionAndAnswerCompositions,
	MemoByHostOfBasicInfoCompositions,
	NonDateOptionsOfPrintComposition,
	OptionListOfRoomWithOptionCompositions,
	PaymentFaqLinkOfManagementStatusCompositions,
	PaymentStatusOfManagementStatusCompositions,
	PeopleOfBasicInfoCompositions,
	PlanLabelOfDetailInfoCompositions,
	PlanNameOfDetailInfoCompositions,
	PlanOfPrintComposition,
	RefundStatusAfterStayOfManagementStatusCompositions,
	RefundStatusOfManagementStatusCompositions,
	RelatedPastReservationsOfBasicInfoCompositions,
	ReplicateReservationButton,
	ReservationIDOfBasicInfoCompositions,
	ReservationStatusOfManagementRequestCompositions,
	ReservationStatusOfManagementStatusCompositions,
	RoomListOfRoomWithOptionCompositions,
	RoomNumOfBasicInfoCompositions,
	TotalGuestChargeOfBasicInfoCompositions,
	TotalGuestChargeOfPrintComposition,
	TransitionEditReservationButtonOfOperationForReservation,
	VoucherOfVoucherAndDiscountCompositions,
} from "./compositions";
import { CardLayout, FeatureIslandLayout, Layout, PrintLayout, SectionLayout } from "./layout";

import type { QueryData } from "../model";

type Props = {
	queryData: QueryData;

	onUpdateReservationDetailReservationRequestHandlingAccept: () => Promise<void>;
	onUpdateReservationDetailReservationRequestHandlingReject: () => Promise<void>;
	onUpdateReservationDetailCancel: (canceledPrice: ReservationCancelInput["canceledPrice"]) => Promise<void>;
	onChargeCancelFee: (chargePrice: ReservationCancelFeeChargeInput["chargePrice"]) => Promise<void>;
	onUpdateReservationDetailUpdateRefundRequest: (
		refundPrice: ReservationRefundRequestInput["refundPrice"],
		refundReason: ReservationRefundRequestInput["refundReason"],
	) => Promise<void>;
	onUpdateReservationLocalCancelFeeTransferAccept: () => Promise<void>;
	onUpdateReservationLocalCancelFeeTransferReject: (
		insufficientAmount: ReservationLocalCancelFeeRejectInput["insufficientAmount"],
		rejectReason: ReservationLocalCancelFeeRejectInput["rejectReason"],
	) => Promise<void>;
	onUpdateReservationDetailUpdateNoShow: (
		statusNoShow: ReservationDetailUpdateNoShowStatusInput["statusNoShow"],
	) => void;
	onIssueReceipt: (address?: string) => Promise<{ pdfUrl: string } | undefined>;
	onUpdateReservationDetailUpdateIssuedReceiptStatus: (
		statusReceiptIssued: ReservationDetailUpdateIssuedReceiptStatusInput["statusReceiptIssued"],
	) => void;
	onUpdateReservationDetailUpdateMailResendRequest: (
		guestEmail: ReservationDetailUpdateMailResendRequestInput["guestEmail"],
		langCode: ReservationDetailUpdateMailResendRequestInput["langCode"],
	) => Promise<void>;
	onUpdateReservationDetailUpdateMailMagazineStatus: (
		statusMailMagazine: ReservationDetailUpdateMailMagazineStatusInput["statusMailMagazine"],
	) => void;
	onUpdateReservationDetailUpdateHostMemo: (hostMemo: ReservationDetailUpdateHostMemoInput["hostMemo"]) => void;
	onClickRepeaterReservation: (hotelID: string, reservationID: string) => void;
};

export const ManagementReservationDetailInner = memo(
	({
		queryData,
		onUpdateReservationDetailReservationRequestHandlingAccept,
		onUpdateReservationDetailReservationRequestHandlingReject,
		onUpdateReservationDetailCancel,
		onChargeCancelFee,
		onUpdateReservationDetailUpdateRefundRequest,
		onUpdateReservationLocalCancelFeeTransferAccept,
		onUpdateReservationLocalCancelFeeTransferReject,
		onUpdateReservationDetailUpdateNoShow,
		onIssueReceipt,
		onUpdateReservationDetailUpdateIssuedReceiptStatus,
		onUpdateReservationDetailUpdateMailResendRequest,
		onUpdateReservationDetailUpdateMailMagazineStatus,
		onUpdateReservationDetailUpdateHostMemo,
		onClickRepeaterReservation,
	}: Props) => {
		/**
		 * 子供の人数、添い寝の子供の人数を返す
		 */
		const onComputeKidsNumDetail = useCallback(
			(
				kidsNumDetail: ReservationDetailForManagementReservationItemQuery["reservationDetail"]["kidsNumDetail"],
			): { kidsNum: number; kidsNumAsCosleeping: number; kidsAgeArray: Array<number> } => {
				return {
					kidsNum: kidsNumDetail.length,
					kidsNumAsCosleeping: kidsNumDetail.filter((item) => item.asCosleeping).length,
					kidsAgeArray: kidsNumDetail.map((item) => item.age),
				};
			},
			[],
		);

		const { kidsNum, kidsNumAsCosleeping, kidsAgeArray } = useMemo(
			() => onComputeKidsNumDetail(queryData.kidsNumDetail),
			[onComputeKidsNumDetail, queryData.kidsNumDetail],
		);

		/**
		 * 予約自体のキャンセルがされたかどうか
		 */
		const canceled = useMemo(() => {
			const statusReservation = queryData.statusReservation;
			return (
				// 決済エラーによる自動キャンセル
				statusReservation === StatusReservation.CancelAutoPaymentError ||
				// 無料キャンセル
				statusReservation === StatusReservation.CancelFree ||
				// キャンセル料金徴収済み
				statusReservation === StatusReservation.CancelFeeCollected ||
				// キャンセル料金未徴収
				statusReservation === StatusReservation.CancelFeeNotCollected
			);
		}, [queryData.statusReservation]);

		/**
		 * 新着リクエストの予約ステータス
		 */
		const statusRequest = useMemo(() => queryData.statusRequest, [queryData.statusRequest]);

		/**
		 * キャンセル可能かどうか
		 */
		const canShowCancel = useMemo(() => {
			return queryData.canCancel;
		}, [queryData.canCancel]);

		/**
		 * キャンセル料金の支払い状況の表示制御
		 */
		const canShowCancellationFeePaymentStatus = useMemo(() => {
			return queryData.canHandleLocalPaymentCancelFee;
		}, [queryData.canHandleLocalPaymentCancelFee]);

		/**
		 * キャンセル料の請求が可能かどうか（与信失敗による強制キャンセル後）
		 */
		const canShowChargeCancelFee = useMemo(() => {
			return queryData.canChargeCancelFee;
		}, [queryData.canChargeCancelFee]);

		/**
		 * 編集可能かどうか
		 */
		const canShowToEditReservationButton = useMemo(() => {
			// 更新可能かどうか
			return queryData.canUpdate;
		}, [queryData.canUpdate]);

		/**
		 * 返金可能かどうか
		 */
		const canShowRefund = useMemo(() => {
			return queryData.canRefund;
		}, [queryData.canRefund]);

		/**
		 * 予約の複製可能かどうか
		 */
		const canShowReplicateButton = useMemo(() => {
			return queryData.isHostDirectReservationAvailable;
		}, [queryData.isHostDirectReservationAvailable]);

		/**
		 * 予約に対する操作の表示制御
		 */
		const canShowHandleReservation = useMemo(() => {
			return (
				canShowCancel ||
				canShowCancellationFeePaymentStatus ||
				canShowChargeCancelFee ||
				canShowToEditReservationButton ||
				canShowRefund ||
				canShowReplicateButton
			);
		}, [
			canShowCancel,
			canShowCancellationFeePaymentStatus,
			canShowChargeCancelFee,
			canShowToEditReservationButton,
			canShowRefund,
			canShowReplicateButton,
		]);

		/**
		 * アンケート結果の表示制御
		 */
		const canShowQuestionAndAnswer = useMemo(
			() => queryData.questionAndAnswerList.length > 0,
			[queryData.questionAndAnswerList],
		);

		/**
		 * プラン
		 */
		const plan = useMemo(() => {
			/**
			 * @todo キャンセルが一部された時の考慮を加える
			 */
			return queryData.aliveItemsPerReservation.plan;
		}, [queryData.aliveItemsPerReservation.plan]);

		/**
		 * 日付に紐づかないオプション
		 */
		const nonDateOptions = useMemo(() => {
			/**
			 * @todo キャンセルが一部された時の考慮を加える
			 */
			return queryData.aliveItemsPerReservation.options;
		}, [queryData.aliveItemsPerReservation.options]);
		/**
		 * 日付に紐づかないオプションの表示制御
		 */
		const canShowNonDateOption = useMemo(() => {
			return nonDateOptions.length > 0;
		}, [nonDateOptions.length]);
		/**
		 * プリント用の日付に紐づかないオプションの表示制御
		 */
		const canShowNonDateOptionOfPrint = useMemo(() => {
			return nonDateOptions.length > 0;
		}, [nonDateOptions.length]);

		/**
		 * 追加料金の表示制御
		 */
		const canShowAdditionalFees = useMemo(() => {
			return queryData.aliveItemsPerReservation.additionalFees.length > 0;
		}, [queryData.aliveItemsPerReservation.additionalFees.length]);

		/**
		 * プリント用の詳細情報の表示制御
		 */
		const canShowDetailInfoOfPrint = useMemo(() => {
			/**
			 * キャンセルされていなければ
			 */
			return !canceled;
		}, [canceled]);

		/**
		 * プリント用の追加料金の表示制御
		 */
		const canShowAdditionalFeesOfPrint = useMemo(() => {
			return queryData.aliveItemsPerReservation.additionalFees.length > 0;
		}, [queryData.aliveItemsPerReservation.additionalFees.length]);

		/**
		 * プリント用の宿泊券の表示制御
		 */
		const canShowVoucherOfPrint = useMemo(() => {
			return queryData.aliveItemsPerReservation.vouchers.length > 0;
		}, [queryData.aliveItemsPerReservation.vouchers.length]);

		/**
		 * キャンセルされた情報の表示制御
		 */
		const canShowCancelInfo = useMemo(() => {
			const { plan, rooms, options } = queryData.deadItems;
			return plan.length > 0 || rooms.length > 0 || options.length > 0;
		}, [queryData.deadItems]);

		/**
		 * キャンセルされた items（print 用）
		 */
		const deadItemsOfPrint = useMemo((): Array<{ name: string; canceledPrice: number }> => {
			const { plan, rooms, options, additionalFees } = queryData.deadItems;

			/**
			 * name, price, actualPrice の取得
			 */
			const deadItems = [
				...plan.map((item) => ({
					name: item.name,
					canceledPrice: item.canceledPrice,
				})),
				...rooms.map((item) => ({
					name: item.name,
					canceledPrice: item.canceledPrice,
				})),
				...options.map((item) => ({
					name: `${item.name} x ${item.amount}個`,
					canceledPrice: item.canceledPrice,
				})),
				...additionalFees.map((item) => ({
					name: item.name,
					canceledPrice: item.canceledPrice,
				})),
			];

			return deadItems;
		}, [queryData.deadItems]);
		/**
		 * キャンセルされた items（print 用） の表示制御
		 */
		const canShowDeadItemsOfPrint = useMemo(() => {
			return deadItemsOfPrint.length > 0;
		}, [deadItemsOfPrint.length]);

		/**
		 * キャンセル料金の合計
		 */
		const totalCancelledPrice = useMemo(() => {
			// キャンセル完了後はtotalGuestChargeがキャンセル料金となる(有償キャンセル時の表示)
			if (canceled) {
				return queryData.totalGuestCharge;
			}

			// キャンセル完了前はdeadItemsの合計(予約変更等でキャンセルされた場合の表示)
			return deadItemsOfPrint.reduce((sum, item) => sum + (item.canceledPrice || 0), 0);
		}, [deadItemsOfPrint, canceled, queryData.totalGuestCharge]);

		return (
			<FeatureIslandContainer>
				<PrintLayout type="hidden">
					{/* ヘッダー */}
					<HeaderComposition />

					<FeatureIslandMain>
						<FeatureIslandLayout>
							{/* リクエスト管理 */}
							{statusRequest && (
								<SectionLayout title="リクエスト管理">
									{/* 予約ステータス */}
									<ReservationStatusOfManagementRequestCompositions
										statusRequest={statusRequest}
										canRequestStatusChange={queryData.canRequestStatusChange}
										//
										onUpdateReservationDetailReservationRequestHandlingAccept={
											onUpdateReservationDetailReservationRequestHandlingAccept
										}
										onUpdateReservationDetailReservationRequestHandlingReject={
											onUpdateReservationDetailReservationRequestHandlingReject
										}
									/>
								</SectionLayout>
							)}

							{/* ステータス管理 */}
							<SectionLayout title="ステータス管理">
								<div className="space-y-3">
									{/* 予約日時・更新日時 */}
									<DatesOfManagementStatusCompositions
										createdAt={queryData.createdAt}
										updatedAt={queryData.updatedAt}
									/>

									{/* 予約ステータス */}
									<ReservationStatusOfManagementStatusCompositions
										statusReservation={queryData.statusReservation}
									/>

									{/* 事前決済ステータス */}
									<PaymentStatusOfManagementStatusCompositions statusPayment={queryData.statusPayment} />

									{/* 返金処理のステータス */}
									<RefundStatusOfManagementStatusCompositions statusRefund={queryData.statusRefund} />

									{/* 決済に関するよくあるお問い合わせ */}
									<PaymentFaqLinkOfManagementStatusCompositions />

									{/* お客様のステータス */}
									<GuestStatusOfManagementStatusCompositions
										statusNoShow={queryData.statusNoShow}
										//
										onUpdate={onUpdateReservationDetailUpdateNoShow}
									/>

									{/* 領収書のステータス */}
									<IssuedReceiptStatusOfManagementStatusCompositions
										statusReceiptIssued={queryData.statusReceiptIssued}
										canIssueReceipt={queryData.canIssueReceipt}
										initialReceiptAddressee={queryData.guestName}
										//
										onUpdate={onUpdateReservationDetailUpdateIssuedReceiptStatus}
										onIssueReceipt={onIssueReceipt}
									/>

									{/* 返金処理のステータス（チェックイン後） */}
									<RefundStatusAfterStayOfManagementStatusCompositions
										statusRefund={queryData.statusRefund}
										refundedPriceByAdmin={queryData.refundedPriceByAdmin}
										totalGuestCharge={queryData.totalGuestCharge}
									/>
								</div>
							</SectionLayout>

							{canShowHandleReservation && (
								<SectionLayout title="予約に対する操作">
									<div className="space-y-8">
										{/* キャンセル料金の支払い状況 */}
										{canShowCancellationFeePaymentStatus && (
											<CancellationFeePaymentStatusOfOperationForReservationComposition
												willCancelPaymentAmount={queryData.totalGuestCharge}
												//
												onUpdateReservationLocalCancelFeeTransferAccept={
													onUpdateReservationLocalCancelFeeTransferAccept
												}
												onUpdateReservationLocalCancelFeeTransferReject={
													onUpdateReservationLocalCancelFeeTransferReject
												}
												localCancelFeeTransferInfo={
													queryData.localCancelFeeTransferInfo ?? undefined
												}
											/>
										)}
										{/* キャンセル */}
										{canShowCancel && (
											<div>
												<ReservationDetailCancelModal
													willCancelPrice={queryData.willCancelPrice ?? undefined}
													//
													onUpdateReservationDetailCancel={onUpdateReservationDetailCancel}
												/>
											</div>
										)}
										{/* キャンセル料の請求（与信失敗による強制キャンセル後） */}
										{canShowChargeCancelFee && queryData.willCancelFeeChargePrice != null && (
											<div>
												<ReservationDetailChargeCancelFeeModal
													willCancelFeeChargePrice={queryData.willCancelFeeChargePrice}
													//
													onChargeCancelFee={onChargeCancelFee}
												/>
											</div>
										)}
										{/* 予約の複製 */}
										{canShowReplicateButton && (
											<div>
												<ReplicateReservationButton
													hotelID={queryData.hotelID}
													reservationID={queryData.reservationID}
													isHostDirectReservationAvailable={
														queryData.isHostDirectReservationAvailable
													}
												/>
											</div>
										)}
										{/* 予約編集 */}
										{canShowToEditReservationButton && (
											<div>
												<TransitionEditReservationButtonOfOperationForReservation
													hotelID={queryData.hotelID}
													reservationID={queryData.reservationID}
												/>
											</div>
										)}
										{/* 返金処理 */}
										{canShowRefund && (
											<ReservationDetailRefundRequest
												totalGuestCharge={queryData.totalGuestCharge}
												//
												onUpdateReservationDetailUpdateRefundRequest={
													onUpdateReservationDetailUpdateRefundRequest
												}
											/>
										)}
									</div>
								</SectionLayout>
							)}

							<SectionLayout title="基本情報">
								<CardLayout>
									<div className="space-y-10">
										{/* 予約番号 */}
										<ReservationIDOfBasicInfoCompositions reservationID={queryData.reservationID} />

										<div className="grid gap-3 lg:grid-flow-col">
											<div className="space-y-6 text-xs 2xl:text-sm">
												{/* ゲスト名（敬称あり） */}
												<GuestNameWithTitleOfBasicInfoCompositions
													guestName={queryData.guestName}
													guestNameKana={queryData.guestNameKana}
												/>

												<div className="space-y-4">
													{/* ゲストの電話番号 */}
													<GuestTelOfBasicInfoCompositions guestTel={queryData.guestTel} />

													{/* ゲストのメールアドレス & 変更/再送 */}
													<GuestEmailOfBasicInfoCompositions
														guestEmail={queryData.guestEmail}
														//
														onUpdate={onUpdateReservationDetailUpdateMailResendRequest}
													/>

													{/* ゲストの住所 */}
													<GuestAddressOfBasicInfoCompositions
														guestPostalCode={queryData.guestPostalCode}
														guestAddress={queryData.guestAddress}
													/>

													{/* 日程 */}
													<DateOfBasicInfoCompositions
														aliveItemsPerDate={queryData.aliveItemsPerDate}
														isDayTrip={queryData.isDayTrip}
													/>

													{/* 人数 */}
													<PeopleOfBasicInfoCompositions
														adultNum={queryData.adultNum}
														kidsNum={kidsNum}
														kidsNumAsCosleeping={kidsNumAsCosleeping}
														kidsAgeArray={kidsAgeArray}
													/>

													{/* 合計部屋数 */}
													<RoomNumOfBasicInfoCompositions roomNum={queryData.roomNum} />
												</div>
											</div>

											<div className="space-y-6">
												{/* メルマガ配信 */}
												<IsMailMagazineAcceptOfBasicInfoCompositions
													isMailMagazineAccept={queryData.isMailMagazineAccept}
													//
													onUpdate={onUpdateReservationDetailUpdateMailMagazineStatus}
												/>

												{/* メモ */}
												<MemoByHostOfBasicInfoCompositions
													memoByHost={queryData.memoByHost}
													//
													onUpdate={onUpdateReservationDetailUpdateHostMemo}
												/>
											</div>
										</div>

										<Layout className="space-y-6">
											{/* 返金前の合計金額 */}
											{/* 宿泊後の返金金額 */}
											{/* お客様の支払い料金合計 */}
											<TotalGuestChargeOfBasicInfoCompositions
												refundedPriceByAdmin={queryData.refundedPriceByAdmin}
												totalGuestCharge={queryData.totalGuestCharge}
											/>
										</Layout>
										{/* 予約履歴 */}
										<Layout className="space-y-6">
											<RelatedPastReservationsOfBasicInfoCompositions
												repeaterReservations={queryData.repeaterReservationList}
												onClickReservation={onClickRepeaterReservation}
											/>
										</Layout>
									</div>
								</CardLayout>
							</SectionLayout>

							{/* アンケート結果 */}
							{canShowQuestionAndAnswer && (
								<SectionLayout title="アンケート結果">
									<CardLayout>
										<ListOfQuestionAndAnswerCompositions
											questionAndAnswerList={queryData.questionAndAnswerList}
										/>
									</CardLayout>
								</SectionLayout>
							)}

							{/* 予約後アンケート（予約完了画面） */}
							{queryData.bookedGuestSurvey != null &&
								queryData.bookedGuestSurvey.answers.length > 0 && (
									<SectionLayout title="予約後アンケート">
										<CardLayout>
											<ListOfBookedGuestSurveyCompositions
												bookedGuestSurvey={queryData.bookedGuestSurvey}
											/>
										</CardLayout>
									</SectionLayout>
								)}

							<SectionLayout title="詳細情報">
								<div className="space-y-6">
									{/* プラン */}
									<CardLayout>
										<div className="space-y-4">
											{/* ラベル */}
											<PlanLabelOfDetailInfoCompositions isDayTrip={queryData.isDayTrip} />

											{/* プラン名 */}
											<PlanNameOfDetailInfoCompositions
												planName={queryData?.aliveItemsPerReservation.plan?.name}
											/>

											{/* 金額 */}
											<ApplyPriceText
												hostInsertedPrice={plan?.hostInsertedPrice ?? undefined}
												discountedPrice={plan?.discountedPrice ?? undefined}
												price={plan?.price}
											/>
										</div>
									</CardLayout>

									{/* 日付に紐づかないオプション */}
									{canShowNonDateOption && (
										<CardLayout>
											<div className="space-y-4">
												{/* ラベル */}
												<Label variant="link" text="日付に紐づかないオプション" />

												{/* 各オプション */}
												<div className="space-y-2">
													<ListOfNonDateOptionCompositions options={nonDateOptions} />
												</div>
											</div>
										</CardLayout>
									)}

									{/* 部屋 & オプション */}
									{queryData.aliveItemsPerDate.length > 0 && (
										<div className="grid grid-flow-col gap-x-4 overflow-x-scroll">
											{queryData.aliveItemsPerDate.map((item, index) => {
												const { date, rooms, options } = item;
												const canShowRooms = rooms.length > 0;
												const canShowOptions = options.length > 0;

												if (!canShowRooms && !canShowOptions) {
													return null;
												}

												return (
													<div key={index} className="min-w-72 max-w-sm">
														<CardLayout>
															<div className="space-y-4">
																{/* 日付 */}
																<DateOfRoomAndOptionCompositions date={date} />

																{/* 部屋 */}
																<div className="space-y-4">
																	{canShowRooms && (
																		<>
																			{/* ラベル */}
																			<Label variant="pink" text="部屋" />

																			<div className="space-y-2">
																				{/* 部屋情報 */}
																				<RoomListOfRoomWithOptionCompositions
																					rooms={rooms}
																					onComputeKidsNumDetail={
																						onComputeKidsNumDetail
																					}
																				/>
																			</div>
																		</>
																	)}

																	{/* オプション */}
																	{canShowOptions && (
																		<div className="space-y-2">
																			{/* ラベル */}
																			<Label variant="link" text="オプション" />

																			<div className="space-y-2">
																				{/* オプション情報 */}
																				<OptionListOfRoomWithOptionCompositions
																					options={options}
																				/>
																			</div>
																		</div>
																	)}
																</div>
															</div>
														</CardLayout>
													</div>
												);
											})}
										</div>
									)}

									{/* 追加料金 */}
									{canShowAdditionalFees && (
										<CardLayout>
											<div className="space-y-4">
												{/* ラベル */}
												<Label variant="black" text="追加料金" />

												{/* 各追加料金 */}
												<div className="space-y-2">
													<ListOfAdditionalFeesCompositions
														adultNum={queryData.adultNum}
														kidsNum={queryData.kidsNum}
														roomNum={queryData.roomNum}
														aliveItemsPerDate={queryData.aliveItemsPerDate}
														additionalFees={
															queryData.aliveItemsPerReservation.additionalFees
														}
													/>
												</div>
											</div>
										</CardLayout>
									)}

									{/* 宿泊券 & ディスカウント */}
									<CardLayout>
										<div className="space-y-4">
											{/* ラベル */}
											<Label variant="black" text="宿泊券 / ディスカウント" />

											<div className="space-y-2">
												{/* 宿泊券 */}
												<VoucherOfVoucherAndDiscountCompositions
													vouchers={queryData.aliveItemsPerReservation.vouchers}
												/>

												{/* ディスカウント */}
												<DiscountOfVoucherAndDiscountCompositions
													discount={queryData.aliveItemsPerReservation.discount}
												/>
											</div>
										</div>
									</CardLayout>
								</div>
							</SectionLayout>

							{/* キャンセル情報 */}
							{canShowCancelInfo && (
								<SectionLayout title="キャンセル情報">
									<div className="space-y-6">
										{/* キャンセル */}
										<CardLayout>
											<div className="space-y-4">
												{/* ラベル */}
												<Label variant="required" text="予約変更 / キャンセル" />

												{/* テキスト */}
												<p className="font-semibold">キャンセル合計料金</p>

												{/* 金額 */}
												<ApplyPriceText cancelledPrice={totalCancelledPrice} />
											</div>
										</CardLayout>

										{/* プラン */}
										{queryData.deadItems.plan.map((plan, index) => (
											<CardLayout key={index}>
												<div className="space-y-4">
													{/* ラベル */}
													<PlanLabelOfDetailInfoCompositions isDayTrip={false} />

													{/* プラン名 */}
													<PlanNameOfDetailInfoCompositions planName={plan.name} />

													{/* 金額 */}
													<ApplyPriceText cancelledPrice={plan?.canceledPrice} />
												</div>
											</CardLayout>
										))}

										{/* 日付に紐づかないオプション */}
										{queryData.deadItems.options.length > 0 && (
											<CardLayout>
												<div className="space-y-4">
													{/* ラベル */}
													<Label variant="link" text="日付に紐づかないオプション" />

													{/* 各オプション */}
													<div className="space-y-2">
														{queryData.deadItems.options.map((option, index) => {
															const optionText = `${option.name} x ${option.amount}個`;
															return (
																<div key={index} className="space-y-4">
																	<p className="font-medium">{optionText}</p>

																	<ApplyPriceText
																		cancelledPrice={option.canceledPrice}
																	/>
																</div>
															);
														})}
													</div>
												</div>
											</CardLayout>
										)}

										{/* 部屋 & オプション */}
										{queryData.deadItems.rooms.length > 0 && (
											<div className="grid grid-flow-col gap-x-4 overflow-x-scroll">
												{queryData.deadItems.rooms.map((item, index) => {
													const {
														date,
														name,
														canceledPrice,
														roomNum: { adultNum, kidsNum: kidsNumDetail },
													} = item;

													const { kidsNum, kidsNumAsCosleeping, kidsAgeArray } =
														onComputeKidsNumDetail(kidsNumDetail);

													return (
														<div key={index} className="min-w-72 max-w-sm">
															<CardLayout>
																<div className="space-y-4">
																	{/* 日付 */}
																	<DateOfRoomAndOptionCompositions date={date} />

																	{/* 部屋 */}
																	<div className="space-y-4">
																		{/* ラベル */}
																		<Label variant="pink" text="部屋" />

																		<div className="space-y-2">
																			<Fragment key={index}>
																				{/* 部屋名 */}
																				<p>{name}</p>

																				{/* 人数 */}
																				<TotalPeopleText
																					adultNum={adultNum}
																					kidsNum={kidsNum}
																					kidsNumAsCosleeping={
																						kidsNumAsCosleeping
																					}
																					kidsAgeArray={kidsAgeArray}
																					className="text-xs"
																				/>

																				{/* 金額 */}
																				<ApplyPriceText
																					cancelledPrice={canceledPrice}
																				/>
																			</Fragment>
																		</div>
																	</div>
																</div>
															</CardLayout>
														</div>
													);
												})}
											</div>
										)}

										{/* 追加料金 */}
										{queryData.deadItems.additionalFees.length > 0 && (
											<CardLayout>
												<div className="space-y-4">
													{/* ラベル */}
													<Label variant="black" text="追加料金" />

													{queryData.deadItems.additionalFees.map((additionalFee, index) => {
														return (
															<div
																key={`${additionalFee.name}-${additionalFee.canceledPrice}-${index}`}
																className="space-y-4"
															>
																<p>{additionalFee.name}</p>
																<ApplyPriceText
																	cancelledPrice={additionalFee.canceledPrice}
																/>
															</div>
														);
													})}
												</div>
											</CardLayout>
										)}
									</div>
								</SectionLayout>
							)}
						</FeatureIslandLayout>
					</FeatureIslandMain>
				</PrintLayout>

				{/* print 用の composition */}
				<PrintLayout type="block">
					<div className="w-full space-y-6 text-xs">
						{/* 基本情報 */}
						<BasicInfoOfPrintComposition
							// 予約番号
							reservationID={queryData.reservationID}
							// お名前
							guestName={queryData.guestName}
							// ふりがな
							guestNameKana={queryData.guestNameKana ?? ""}
							// メールアドレス
							guestEmail={queryData.guestEmail}
							// 電話番号
							guestTel={queryData.guestTel}
							// ご住所
							guestPostalCode={queryData.guestPostalCode}
							guestAddress={queryData.guestAddress}
							// 部屋数
							roomNum={queryData.roomNum}
							// 人数
							adultNum={queryData.adultNum}
							kidsNum={queryData.kidsNum}
							// ご宿泊日
							aliveItemsPerDate={queryData.aliveItemsPerDate}
							isDayTrip={queryData.isDayTrip}
						/>

						{/* 詳細情報 */}
						{canShowDetailInfoOfPrint && (
							<>
								{/* プラン */}
								<PlanOfPrintComposition
									isDayTrip={queryData.isDayTrip}
									name={plan?.name}
									hostInsertedPrice={plan?.hostInsertedPrice ?? undefined}
									discountedPrice={plan?.discountedPrice ?? undefined}
									price={plan?.price}
								/>

								{/* 日ごと */}
								<EachDaysOfPrintComposition items={queryData.aliveItemsPerDate} />

								{/* 日ごとに紐づかないオプション */}
								{canShowNonDateOptionOfPrint && (
									<NonDateOptionsOfPrintComposition options={nonDateOptions} />
								)}

								{/* 追加料金 */}
								{canShowAdditionalFeesOfPrint && (
									<AdditionalFeesOfPrintComposition
										additionalFees={queryData.aliveItemsPerReservation.additionalFees}
									/>
								)}

								{/* ディスカウント */}
								{/* 宿泊券 */}
								{canShowVoucherOfPrint && (
									<DiscountOfPrintComposition
										type="voucher"
										vouchers={queryData.aliveItemsPerReservation.vouchers}
									/>
								)}

								{/* ディスカウント（通常・連泊・複合） */}
								{/* 型補完をさせるために明示的に指定している */}
								{queryData.aliveItemsPerReservation.discount && (
									<DiscountOfPrintComposition
										type="discount"
										discount={queryData.aliveItemsPerReservation.discount}
									/>
								)}

								{/* キャンセル料金（部分的） */}
								{canShowDeadItemsOfPrint && <DeadItemsOfPrintComposition items={deadItemsOfPrint} />}
							</>
						)}

						{/* お支払い料金 */}
						<TotalGuestChargeOfPrintComposition
							canceled={canceled}
							totalGuestCharge={queryData.totalGuestCharge}
						/>

						{/* キャンセルされていなければ、入力項目は二枚目として可能にする */}
						<div className={cn(!canceled && "break-before-page")}>
							{/* 留意事項（メモ） */}
							<HostMemoOfPrintComposition />

							{/* 宿泊者記入欄 */}
							<EntryFormForGuestOfPrintComposition />
						</div>
					</div>
				</PrintLayout>
			</FeatureIslandContainer>
		);
	},
);

ManagementReservationDetailInner.displayName = "ManagementReservationDetailInner";
